import {
    AfterViewInit,
    ComponentRef,
    Directive,
    EventEmitter,
    Input,
    isDevMode,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewContainerRef,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { AppHelper } from '@app/core/app.helper';
import { IFieldSelect } from '@app/core/libraries/Dynamic-fields/components/select/interfaces/FieldSelect.interface';
import { AngularEditorComponent } from '@components/angular-editor/angular-editor.component';
import { ButtonComponent } from '@components/button/component/button.component';
import { AddressComponent } from '../address/component/address.component';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { ColorComponent } from '../color/color.component';
import { DateMonthComponent } from '../date-month/date-month.component';
import { DateRangeDDMMComponent } from '../date-range-dd-mm/date-range.component';
import { DateRangeComponent } from '../date-range/date-range.component';
import { DateTimeComponent } from '../date-time/date-time.component';
import { DateYearComponent } from '../date-year/date-year.component';
import { DateComponent } from '../date/date.component';
import { DayMonthComponent } from '../day-month/day-month.component';
import { EmojiComponent } from '../emoji/emoji.component';
import { FieldConfig, ICommandEvent, ITrailingButtonEvent } from '../field.interface';
import { InputTreeSelectComponent } from '../input-tree-select/input-tree-select.component';
import { InputComponent } from '../input/input.component';
import { MonthComponent } from '../month/month.component';
import { RadioButtonComponent } from '../radio-button/radio-button.component';
import { SelectSearchComponent } from '../select-search/select-search.component';
import { SelectSearchLoadComponent } from '../select/select-search-load/select-search-load.component';
import { SelectTreeComponent } from '../select/select-tree/select-tree.component';
import { SelectComponent } from '../select/select.component';
import { SlideToggleComponent } from '../slide-toggle/slide-toggle.component';
import { SliderComponent } from '../slider/slider.component';
import { TextComponent } from '../text/text.component';
import { WysiwygComponent } from '../wysiwyg/wysiwyg.component';

const componentMapper = {
    input: InputComponent,
    button: ButtonComponent,
    select: SelectComponent,
    'select-search': SelectSearchComponent,
    'select-tree-load': SelectTreeComponent,
    date: DateComponent,
    radiobutton: RadioButtonComponent,
    checkbox: CheckboxComponent,
    text: TextComponent,
    wysiwyg: WysiwygComponent,
    address: AddressComponent,
    'select-tree': InputTreeSelectComponent,
    'date-month': DateMonthComponent,
    'date-year': DateYearComponent,
    'date-time': DateTimeComponent,
    month: MonthComponent,
    'day-month': DayMonthComponent,
    color: ColorComponent,
    emoji: EmojiComponent,
    'date-range': DateRangeComponent,
    slider: SliderComponent,
    'slider-toggle': SlideToggleComponent,
    'angular-editor': AngularEditorComponent,
    'select-search-load': SelectSearchLoadComponent,
    'date-range-ddmm': DateRangeDDMMComponent,
};

@Directive({
    selector: '[dynamic-field]',
    exportAs: 'dynamic-field',
})
export class DynamicFieldDirective implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    constructor(private container: ViewContainerRef) {}

    /** Возвращает состояние компонента */
    get componentInstance() {
        return this.componentRef?.instance;
    }

    @Input()
    get data(): any {
        return this._data;
    }

    set data(value: any) {
        this._data = value;
        if (this.componentRef) {
            this.componentRef.instance.data = this.data;
            if (this.componentRef.instance.changeData) {
                this.componentRef.instance.changeData();
            }
        }
    }
    private _data: any;

    /** Текущий язык формы */
    @Input() currentLangForm: { name?: string; code?: string } = {};
    componentRef: ComponentRef<any>;
    @Input() id: string;
    @Input() field: FieldConfig | IFieldSelect;
    @Input() group: FormGroup | AbstractControl;
    @Input() focusSelectorAfterInit: string = null;
    @Output() eventAdd = new EventEmitter<any>();
    @Output() eventUpdateList = new EventEmitter<any>();
    @Output() eventCommand = new EventEmitter<ICommandEvent>();
    @Output() outputEvent: EventEmitter<ITrailingButtonEvent> = new EventEmitter<ITrailingButtonEvent>();

    /**
     * Получить ключ к карте компонентов по id формату даты
     * @param dateFormatId - id формат даты
     */
    public static getDateTypeAlias(dateFormatId: number): string {
        switch (dateFormatId) {
            case 2:
                return 'date-month';
            case 3:
                return 'date-year';
            case 4:
                return 'month';
            case 5:
                return 'date-time';
            case 6:
                return 'day-month';
            default:
                return 'date';
        }
    }

    ngOnDestroy(): void {
        this.outputEvent?.complete();
    }

    ngOnInit() {
        if (!componentMapper[this.field.type]) {
            if (isDevMode()) {
                console.error(this);
            }
            return;
        }

        this.componentRef = this.container.createComponent(componentMapper[this.field.type]);
        switch (this.field.type) {
            case 'select-tree-load':
            case 'select-search-load':
            case 'select':
                this.componentRef.instance.service.field = this.field;
                this.componentRef.instance.service.group = this.group;
                this.componentRef.instance.service.config = this.data;
                break;
            default:
                this.componentRef.instance.field = this.field;
                this.componentRef.instance.group = this.group;
        }
        this.componentRef.instance.data = this.data;
        this.componentRef.instance.eventAdd = this.eventAdd;
        this.componentRef.instance.currentLangForm = this.currentLangForm;
        this.componentRef.instance.eventUpdateList = this.eventUpdateList;
        if (this.componentRef.instance.hasOwnProperty('outputEvent') && this.field?.trailingButton?.action) {
            this.componentRef.instance.outputEvent = this.outputEvent;
        }
        if (this.componentRef.instance.hasOwnProperty('eventCommand')) {
            this.componentRef.instance.eventCommand = this.eventCommand;
        }
    }

    /**
     * Событие изменения данных
     * @param changes изменения
     */
    ngOnChanges(changes: SimpleChanges) {
        if (this.componentRef && changes.currentLangForm) {
            this.componentRef.instance.currentLangForm = changes.currentLangForm.currentValue;
        }
        if (this.componentRef && changes.group) {
            this.componentRef.instance.group = changes.group.currentValue;
        }
        if (this.componentRef && changes.field) {
            this.componentRef.instance.field = changes.field.currentValue;
            if (this.componentRef.instance.setDisabled) {
                this.componentRef.instance.setDisabled(this.componentRef.instance.field.disabled);
            }
            if (this.componentRef.instance.setOptions) {
                this.componentRef.instance.setOptions(this.componentRef.instance.field.options);
            }
        }
        if (this.componentRef && changes.eventAdd) {
            this.componentRef.instance.eventAdd = changes.eventAdd.currentValue;
        }

        if (this.componentRef?.instance?.ngOnChanges) {
            this.componentRef?.instance?.ngOnChanges(changes);
        }
    }

    /**
     * Установка опций селекта
     * @param options
     */
    setOptions = (options) => {
        this.componentRef.instance.setOptions(options);
    };

    /**
     * Установка файлов
     * @param files
     */
    setFiles(files) {
        this.componentRef.instance.setFiles(files);
    }

    /**
     * Закрытие элемента
     */
    close() {
        this.componentRef.instance.close();
    }

    /**
     * Обновление данных компонента
     */
    getData() {
        this.componentRef.instance.getData();
    }

    public ngAfterViewInit(): void {
        // Установка фокуса на элемент, после его инициализации
        if (this.focusSelectorAfterInit) {
            const target = this.componentRef?.location?.nativeElement?.querySelector(this.focusSelectorAfterInit);
            AppHelper.focusInput(target);
            this.group?.get(this.field?.name)?.markAsTouched({ onlySelf: true });
        }
    }

    /** Метод для уничтожения представления */
    public destroy() {
        this.container.remove(0);
    }
}
